/* eslint-disable @typescript-eslint/no-explicit-any */
import { FC, useCallback, useEffect, useState } from 'react'
import { cn } from '@bem-react/classname'
import { Helmet } from 'react-helmet'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { parse, stringify } from 'query-string'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import countryList from 'react-select-country-list'

import { Memo } from '@/hoc/Memo'
import yup from '@/utils/yup-extended'
import useBackendError from '@/hooks/useBackendError'
import { useAppDispatch, useAppSelector } from '@/core/store/hooks'
import {
  getDeviceSerialThunk,
  getLicenseVersionsThunk,
  licenseVersionsPriceWithOptionsThunk,
} from '@/core/services/LicenseVersions/store/license-versions.slice'
import { changeLoading } from '@/core/services/Loading/store/loading.slice'
import { licensesDeviceSerialSelector } from '@/core/services/LicenseVersions/store/license-versions.selector'

import { Back } from '@/components/Back'
import { Block } from '@/components/Block'
import { Checkbox } from '@/components/form/Checkbox'
import { Button } from '@/components/Button'
import { FormGroup } from '@/components/form/FormGroup'
import { Field } from '@/components/form/Field'
import { Select } from '@/components/form/Select'

import './styles/ProductsPage.scss'
import { roleSelector } from '../AccountPage/store/accont.selector'
import { RoleUserModel } from '@/core/services/Token/model/TokenModel'
import { setToast } from '@/core/services/Toast/store/toast.slice'
import { ToastType } from '@/core/services/Toast/types/ToastType'
import { getItem, setItem } from '@/utils/persistanceStorage'
import { CountryDataModel } from '../AccountPage/models/UserModel'
import { Modal } from '@/components/Modal'
import { CompanyCodes } from './CompanyCodes'
import axiosApiInstance from '@/api/axios'
import { LicensesByCustomerEmailModel } from '../LicensesPage/models/LicensesModel'
import { LICENSE_VERSION_LABELS } from '@/config/constants'
import { LicenseVersionsProductModel } from '@/core/services/LicenseVersions/models/LicenseVersionsModel'

const cnProducts = cn('Products')

async function licensesByCustomerEmail(
  customerEmail: string,
): Promise<LicensesByCustomerEmailModel[]> {
  const res = await axiosApiInstance.get(
    `/Licenses/byCustomerEmail?cvustomerEmail=${customerEmail}`,
  )

  return res.data
}

export const ProductsPage: FC = Memo(() => {
  const { t, i18n, ready } = useTranslation(['translation', 'countries'])
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { search } = useLocation()
  const [setBackendError, fieldsError, resetErrors] = useBackendError()
  const licensesDeviceSerial = useAppSelector(licensesDeviceSerialSelector)
  const role = useAppSelector(roleSelector)
  const [countries, setCountries] = useState<CountryDataModel[]>([])
  const nameRegexCustomer = /^[A-Za-z0-9\s]+$/
  const [isOpenLicensesCustomerEmailInfo, setOpenLicensesCustomerEmailInfo] =
    useState<boolean>(false)
  const [companyCodesArrayState, setCompanyCodesArrayState] = useState<
    string[]
  >([])

  const loadLicenses = useCallback(
    async (serial: string | null) => {
      try {
        dispatch(changeLoading(true))

        if (serial) {
          await dispatch(getDeviceSerialThunk(serial)).unwrap()
          return
        }

        await dispatch(getLicenseVersionsThunk()).unwrap()
      } catch (errors) {
        setBackendError(errors)
      } finally {
        dispatch(changeLoading(false))
      }
    },
    [dispatch],
  )

  useEffect(() => {
    const list = countryList().getData()
    const upd = list.map(el => {
      return {
        ...el,
        label: t(`countries:${el.value}`),
      }
    })
    if (i18n.language === 'en-GB') setCountries(list)
    else setCountries(upd)
  }, [i18n, ready])

  useEffect(() => {
    const s = parse(search)
    const serial = (s.serialNumber as string) || null

    loadLicenses(serial)
  }, [search])

  const ProductSchema = yup.object().shape({
    discountCode: yup.string().trim().nullable(),
    licenseVersionId: yup.string().trim().required(),
  })

  const ProductFullSchema = yup.object().shape({
    discountCode: yup.string().trim().nullable(),
    licenseVersionId: yup.string().trim().required(),
    customerName: yup
      .string()
      .trim()
      .nullable()
      .required(t('This field is required'))
      .matches(
        nameRegexCustomer,
        t(
          'Only English letters and numbers. Special characters (except spaces) not allowed',
        ),
      )
      .min(
        1,
        t('The value must be at least {{value}} characters long', {
          value: 1,
        }),
      ),
    customerSurname: yup
      .string()
      .trim()
      .nullable()
      .required(t('This field is required'))
      .matches(
        nameRegexCustomer,
        t(
          'Only English letters and numbers. Special characters (except spaces) not allowed',
        ),
      )
      .min(
        1,
        t('The value must be at least {{value}} characters long', {
          value: 1,
        }),
      ),
    customerFarm: yup
      .string()
      .trim()
      .nullable()
      .required(t('This field is required'))
      .matches(
        nameRegexCustomer,
        'Only English letters and numbers. Special characters (except spaces) not allowed',
      )
      .min(
        4,
        t('The value must be at least {{value}} characters long', {
          value: 4,
        }),
      )
      .max(
        40,
        t('The value must contain no more than {{value}} characters.', {
          value: 40,
        }),
      ),
    customerPhone: yup
      .string()
      .trim()
      .when({
        is: (val: string) => val.length > 0,
        then: yup.string().trim().isValidPhone(t('isValidPhone')),
      })
      .nullable()
      .required(t('This field is required')),

    customerAddress: yup.string().trim().nullable(),
    customerEmail: yup
      .string()
      .trim()
      .email(t('Email is not correct'))
      .required(t('This field is required')),
    countryCode: yup
      .string()
      .typeError(t('This field is required'))
      .required(t('This field is required')),
    note: yup
      .string()
      .trim()
      .nullable()
      .max(
        150,
        t('The value must contain no more than {{value}} characters.', {
          value: 150,
        }),
      ),
  })
  const {
    register,
    handleSubmit,
    watch,
    reset,
    control,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(
      role === RoleUserModel.Reseller ? ProductFullSchema : ProductSchema,
    ),
  })

  useEffect(() => {
    const form = getItem('form')
    if (form) {
      reset(form)
    }
  }, [])

  useEffect(() => {
    if (errors && errors.licenseVersionId) {
      dispatch(
        setToast({
          type: ToastType.error,
          message: t('You have not selected a licence'),
        }),
      )
    }
  }, [errors])

  const chooseProduct = useCallback(
    async data => {
      try {
        dispatch(changeLoading(true))

        const currentLicense = licensesDeviceSerial.find(
          el => el.licenseVersionId === data.licenseVersionId,
        )
        const customerEmailInfo = await licensesByCustomerEmail(
          data.customerEmail,
        )

        const isAlreadyActive = currentLicense && currentLicense.isAlreadyActive

        const companyCodesArray = customerEmailInfo
          .filter(item => item.companyCode !== null)
          .map(item => item.companyCode)

        if (companyCodesArray.length > 0) {
          setCompanyCodesArrayState(companyCodesArray)
          setOpenLicensesCustomerEmailInfo(true)
          dispatch(changeLoading(false))
          return
        }

        await dispatch(
          licenseVersionsPriceWithOptionsThunk({
            ...data,
            licenseVresionIds: [data.licenseVersionId],
          }),
        ).unwrap()

        dispatch(changeLoading(false))

        const str = parse(search)
        const query = stringify({ ...str, ...data })

        if (isAlreadyActive && role !== RoleUserModel.Reseller) {
          navigate(
            `/cabinet/licenses/subscription?licenseId=${currentLicense.latestLicenseId}&discountCode=${data.discountCode}`,
          )

          setItem('form', { ...str, ...data })
        } else {
          navigate(`/cabinet/licenses/subscription?${query}`)

          setItem('form', { ...str, ...data })
        }
      } catch (errors) {
        setBackendError(errors)
      } finally {
        dispatch(changeLoading(false))
      }
    },
    [search, licensesDeviceSerial],
  )

  useEffect(() => {
    const subscription = watch((form, { type }) => {
      if (type === 'change' && form.discountCode.length === 0) {
        resetErrors && resetErrors()
      }
    })
    return () => subscription.unsubscribe()
  }, [watch])

  const licenseSerialUser = licensesDeviceSerial.filter(
    item =>
      item.licenseVersionId !== 'advanced' && item.licenseVersionId !== 'ic',
  )

  const handleCancelModal = useCallback(() => {
    setOpenLicensesCustomerEmailInfo(false)
    navigate('/cabinet/licenses/')
  }, [navigate])

  const allowBuyLicense = useCallback(
    async data => {
      try {
        const currentLicense = licensesDeviceSerial.find(
          el => el.licenseVersionId === data.licenseVersionId,
        )

        const isAlreadyActive = currentLicense && currentLicense.isAlreadyActive

        await dispatch(
          licenseVersionsPriceWithOptionsThunk({
            ...data,
            licenseVresionIds: [data.licenseVersionId],
          }),
        ).unwrap()
        dispatch(changeLoading(true))
        const str = parse(search)
        const query = stringify({ ...str, ...data })

        if (isAlreadyActive && role !== RoleUserModel.Reseller) {
          navigate(
            `/cabinet/licenses/subscription?licenseId=${currentLicense.latestLicenseId}&discountCode=${data.discountCode}`,
          )

          setItem('form', { ...str, ...data })
        } else {
          navigate(`/cabinet/licenses/subscription?${query}`)

          setItem('form', { ...str, ...data })
        }
      } catch (errors) {
        setBackendError(errors)
      }
    },
    [search, licensesDeviceSerial],
  )

  const handleConfirmModal = () => {
    const data = getValues()
    setOpenLicensesCustomerEmailInfo(false)
    allowBuyLicense(data)
  }

  const getProductLabel = (product: LicenseVersionsProductModel) => {
    return (
      LICENSE_VERSION_LABELS[product.licenseVersion.id] ||
      product.licenseVersion.name
    )
  }

  return (
    <>
      <Helmet>
        <title>{t('Products')} | Registration</title>
      </Helmet>
      <div className={cnProducts()}>
        <Back />

        <form
          className={cnProducts('form', {
            center: role !== RoleUserModel.Reseller,
          })}
          onSubmit={handleSubmit(chooseProduct)}
        >
          <div
            className={cnProducts('wrap', {
              column: role === RoleUserModel.Reseller,
            })}
          >
            <div className={cnProducts('column')}>
              {role === RoleUserModel.User ? (
                <div className={cnProducts('grid')}>
                  {licenseSerialUser &&
                    licenseSerialUser.map(el => (
                      <Block
                        key={el.licenseVersionId}
                        className={cnProducts('item')}
                        type={
                          errors && errors.licenseVersionId
                            ? 'error'
                            : 'default'
                        }
                      >
                        <Checkbox
                          {...register('licenseVersionId')}
                          type='radio'
                          value={el.licenseVersionId}
                          label={getProductLabel(el)}
                        />
                      </Block>
                    ))}
                </div>
              ) : (
                <div className={cnProducts('grid')}>
                  {licensesDeviceSerial &&
                    licensesDeviceSerial.map(el => (
                      <Block
                        key={el.licenseVersionId}
                        className={cnProducts('item')}
                        type={
                          errors && errors.licenseVersionId
                            ? 'error'
                            : 'default'
                        }
                      >
                        <Checkbox
                          {...register('licenseVersionId')}
                          type='radio'
                          value={el.licenseVersionId}
                          label={getProductLabel(el)}
                        />
                      </Block>
                    ))}
                </div>
              )}

              <FormGroup>
                <Field
                  {...register('discountCode')}
                  placeholder={t('Please enter Discount code')}
                  label={t('Discount Code')}
                  errors={
                    (errors.discountCode && errors.discountCode.message) ||
                    (fieldsError &&
                      fieldsError.discountCode &&
                      fieldsError.discountCode.message)
                  }
                />
              </FormGroup>
            </div>
            {role === RoleUserModel.Reseller && (
              <div className={cnProducts('column', { grid: true })}>
                <Field
                  {...register('customerName')}
                  placeholder={t('Customer Name')}
                  label={t('Customer Name')}
                  required
                  errors={
                    (errors.customerName && errors.customerName.message) ||
                    (fieldsError &&
                      fieldsError.customerName &&
                      fieldsError.customerName.message)
                  }
                />
                <Field
                  {...register('customerSurname')}
                  placeholder={t('Customer Surname')}
                  label={t('Customer Surname')}
                  required
                  errors={
                    (errors.customerSurname &&
                      errors.customerSurname.message) ||
                    (fieldsError &&
                      fieldsError.customerSurname &&
                      fieldsError.customerSurname.message)
                  }
                />

                <Field
                  {...register('customerFarm')}
                  placeholder={t('Farm Name')}
                  label={t('Farm Name')}
                  required
                  errors={
                    (errors.customerFarm && errors.customerFarm.message) ||
                    (fieldsError &&
                      fieldsError.customerFarm &&
                      fieldsError.customerFarm.message)
                  }
                />

                <Select
                  label={t('Customer Country')}
                  placeholder={t('(None)')}
                  control={control}
                  name='countryCode'
                  options={countries}
                  optionLabel='label'
                  optionValue='value'
                  required
                  errors={
                    (errors.countryCode && errors.countryCode.message) ||
                    (fieldsError &&
                      fieldsError.countryCode &&
                      fieldsError.countryCode.message)
                  }
                  isNone
                />
                <Field
                  {...register('customerEmail')}
                  placeholder={t('Customer Email')}
                  label={t('Customer Email')}
                  required
                  errors={
                    (errors.customerEmail && errors.customerEmail.message) ||
                    (fieldsError &&
                      fieldsError.customerEmail &&
                      fieldsError.customerEmail.message)
                  }
                />
                <Field
                  {...register('customerPhone')}
                  placeholder={t('Customer Phone')}
                  label={t('Customer Phone')}
                  required
                  errors={
                    (errors.customerPhone && errors.customerPhone.message) ||
                    (fieldsError &&
                      fieldsError.customerPhone &&
                      fieldsError.customerPhone.message)
                  }
                />
                <Field
                  {...register('customerAddress')}
                  placeholder={t('Customer Address')}
                  label={t('Customer Address')}
                  errors={
                    (errors &&
                      errors.customerAddress &&
                      errors.customerAddress.message) ||
                    (fieldsError &&
                      fieldsError.customerAddress &&
                      fieldsError.customerAddress.message)
                  }
                />

                <Field
                  {...register('note')}
                  placeholder={t('Note')}
                  label={t('Note')}
                  errors={
                    (errors && errors.note && errors.note.message) ||
                    (fieldsError &&
                      fieldsError.note &&
                      fieldsError.note.message)
                  }
                />
              </div>
            )}
          </div>

          <div className={cnProducts('btn')}>
            <Button large type='submit' isLoading={isSubmitting}>
              {t('Submit')}
            </Button>
          </div>
        </form>
      </div>
      <Modal
        isOpen={isOpenLicensesCustomerEmailInfo}
        onClose={handleCancelModal}
        onConfirm={handleConfirmModal}
        isConfirm
        titlePosition='center'
        buttonPosition='center'
        yesBtnText={t('Yes')}
        closeBtnText={t('No')}
        size='md'
      >
        <CompanyCodes companycodes={companyCodesArrayState} />
      </Modal>
    </>
  )
})
